/* istanbul ignore file */
import { CreatePageLayout } from 'framework/ui/layout';
import { useEffect, useState } from 'react';
import useOpportunityService, { DAYS_TO_CLOSE, NEW } from 'domain/sales/opportunity/opportunityService';
import OpportunityForm, { transformFormData } from 'domain/sales/opportunity/createupdate/OpportunityForm';
import { transformChecklistData } from 'domain/sales/shared/checklist/OpportunityChecklist';
import { CreateForm, BOOLEAN_VALUE } from 'framework/form';
import { ACCOUNT_TYPE } from 'domain/sales/account/shared/input/AccountInput';
import { Loading } from 'framework/ui/mui-builder';
import { useAccountResourceService } from 'domain/shared/account';
import { AccountDataType } from 'domain/sales/account/type';
import { useSearchParameter, useRelatedRedirect } from 'framework/routing';
import { useDateService, DATE_FORMAT } from 'framework/utils';
import { useOpportunityUrl } from 'domain/sales/opportunity/opportunityRouting';
import CreateOpportunityBreadcrumbs from 'domain/sales/opportunity/createupdate/CreateOpportunity/Breadcrumbs';
import { ENTITY_DOCUMENT_TYPE } from 'domain/sales/opportunity/shared/constants';
import { useCompositeDocumentStorageService } from 'domain/shared/documentStorage';
import useAccountUrl from 'domain/sales/account/useAccountUrl';

const CreateOpportunity = () => {
  const { create, getValidationSchema } = useOpportunityService();
  const accountId = useSearchParameter('accountId');
  const accountName = useSearchParameter('accountName');
  const isContractRenewal = useSearchParameter('isContractRenewal');
  const relatedProjectId = useSearchParameter('relatedProjectId');
  const { getCurrentDateTimeInUtc } = useDateService();
  const currentDate = getCurrentDateTimeInUtc();
  const initialCloseDate = currentDate.add(DAYS_TO_CLOSE, 'days').format(DATE_FORMAT.INPUT_VALUE);
  const [accountInfo, setAccountInfo] = useState<{ id: string; name: string } | null>(null);
  const { getById } = useAccountResourceService();
  const { commitDocument } = useCompositeDocumentStorageService();

  const isContractRenewalValue = isContractRenewal ? BOOLEAN_VALUE.YES : undefined;

  useEffect(() => {
    if (accountId) {
      getById(accountId).then((account: AccountDataType) => {
        setAccountInfo({ id: accountId, name: account.accountName });
      });
    } else {
      setAccountInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const { getRelatedOpportunitiesUrl } = useAccountUrl();
  const { getListUrl } = useOpportunityUrl();

  const { redirect } = useRelatedRedirect({
    message: 'You are awesome! The Opportunity has been successfully created.',
    changedUrl: getListUrl(),
    unchangedUrl: getRelatedOpportunitiesUrl(accountId!),
    fields: {
      accountId,
    },
  });

  if (accountId && !accountInfo) {
    return <Loading />;
  }

  return (
    <CreateForm
      submitFormFunc={async data => {
        const transformedData = transformFormData(data);

        if (transformedData?.opportunityChecklist) {
          transformedData.opportunityChecklist = transformChecklistData(transformedData.opportunityChecklist);
        }
        const artifactPath = data?.artifactPaths;
        const commitData = await commitDocument(artifactPath, ENTITY_DOCUMENT_TYPE.ENTITY_DOCUMENT);

        return create({ ...transformedData, artifactPaths: commitData });
      }}
      initialData={{
        accountId,
        isContractRenewal: isContractRenewalValue,
        accountName: accountInfo?.name ?? accountName,
        accountCodeName: accountInfo?.name,
        accountType: accountId || isContractRenewalValue ? ACCOUNT_TYPE.EXISTING : ACCOUNT_TYPE.NEW,
        originalAccountName: '',
        stateStep: NEW,
        closeDate: initialCloseDate,
        relatedProject: relatedProjectId || '',
      }}
      afterSubmit={{
        execute: redirect,
      }}
      getValidationSchemaFunc={getValidationSchema}
    >
      <CreatePageLayout title="Create: Opportunity" breadcrumbs={<CreateOpportunityBreadcrumbs />}>
        <OpportunityForm isCreateForm />
      </CreatePageLayout>
    </CreateForm>
  );
};

export default CreateOpportunity;
